import React, { useState } from 'react';

interface ConversionOptions {
  includeAttributes: boolean;
  includeTextNodes: boolean;
  arrayMode: 'auto' | 'always' | 'never';
  indentSize: number;
}

const XmlToJson: React.FC = () => {
  const [xmlInput, setXmlInput] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [error, setError] = useState('');
  const [options, setOptions] = useState<ConversionOptions>({
    includeAttributes: true,
    includeTextNodes: true,
    arrayMode: 'auto',
    indentSize: 2
  });

  const parseXml = (xmlString: string): Document => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(xmlString, 'text/xml');
    
    if (doc.getElementsByTagName('parsererror').length > 0) {
      throw new Error('Invalid XML format');
    }
    
    return doc;
  };

  const xmlToJson = (node: Node, options: ConversionOptions): any => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent?.trim();
      return text || null;
    }
    
    if (node.nodeType === Node.ELEMENT_NODE) {
      const element = node as Element;
      const result: any = {};
      
      // Handle attributes
      if (options.includeAttributes && element.attributes.length > 0) {
        for (let i = 0; i < element.attributes.length; i++) {
          const attr = element.attributes[i];
          result[`@${attr.name}`] = attr.value;
        }
      }
      
      // Handle child nodes
      const children: any[] = [];
      let hasTextContent = false;
      
      for (let i = 0; i < element.childNodes.length; i++) {
        const child = element.childNodes[i];
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent?.trim();
          if (text) {
            hasTextContent = true;
            if (options.includeTextNodes) {
              children.push({ '#text': text });
            }
          }
        } else if (child.nodeType === Node.ELEMENT_NODE) {
          children.push(xmlToJson(child, options));
        }
      }
      
      // Handle text content
      if (hasTextContent && !options.includeTextNodes) {
        const textContent = element.textContent?.trim();
        if (textContent) {
          result['#text'] = textContent;
        }
      }
      
      // Merge children
      for (const child of children) {
        if (typeof child === 'object' && child !== null) {
          for (const [key, value] of Object.entries(child)) {
            if (result[key]) {
              // Handle duplicate keys
              if (Array.isArray(result[key])) {
                result[key].push(value);
              } else {
                result[key] = [result[key], value];
              }
            } else {
              result[key] = value;
            }
          }
        }
      }
      
      // Handle array mode
      if (options.arrayMode === 'always') {
        return [result];
      } else if (options.arrayMode === 'never') {
        return result;
      } else {
        // Auto mode - convert to array if multiple children with same name
        const processed: any = {};
        for (const [key, value] of Object.entries(result)) {
          if (Array.isArray(value)) {
            processed[key] = value;
          } else {
            processed[key] = value;
          }
        }
        return processed;
      }
    }
    
    return null;
  };

  const handleConvert = () => {
    if (!xmlInput.trim()) {
      setError('请输入XML数据');
      return;
    }
    
    try {
      setError('');
      const doc = parseXml(xmlInput);
      const rootElement = doc.documentElement;
      const json = xmlToJson(rootElement, options);
      
      const indent = ' '.repeat(options.indentSize);
      const jsonString = JSON.stringify(json, null, indent);
      setJsonOutput(jsonString);
    } catch (err) {
      setError(err instanceof Error ? err.message : '转换失败');
      setJsonOutput('');
    }
  };

  const handleClear = () => {
    setXmlInput('');
    setJsonOutput('');
    setError('');
  };

  const handleCopyJson = () => {
    if (jsonOutput) {
      navigator.clipboard.writeText(jsonOutput);
    }
  };

  const handleDownloadJson = () => {
    if (jsonOutput) {
      const blob = new Blob([jsonOutput], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'converted.json';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleSampleXml = () => {
    const sample = `<?xml version="1.0" encoding="UTF-8"?>
<data>
  <person id="123" type="employee">
    <name>张三</name>
    <age>30</age>
    <email>zhangsan@example.com</email>
    <address>
      <street>中关村大街</street>
      <city>北京</city>
      <country>中国</country>
    </address>
    <hobbies>
      <hobby>读书</hobby>
      <hobby>游泳</hobby>
      <hobby>编程</hobby>
    </hobbies>
    <active>true</active>
  </person>
</data>`;
    setXmlInput(sample);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-800 mb-4">XML转JSON工具</h1>
          <p className="text-lg text-gray-600">将XML数据转换为JSON格式，支持多种转换选项</p>
        </div>

        {/* Conversion Options */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">转换选项</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="flex items-center">
              <input
                id="includeAttributes"
                type="checkbox"
                checked={options.includeAttributes}
                onChange={(e) => setOptions(prev => ({ ...prev, includeAttributes: e.target.checked }))}
                className="mr-2"
              />
              <label htmlFor="includeAttributes" className="text-sm text-gray-700">
                包含属性
              </label>
            </div>
            
            <div className="flex items-center">
              <input
                id="includeTextNodes"
                type="checkbox"
                checked={options.includeTextNodes}
                onChange={(e) => setOptions(prev => ({ ...prev, includeTextNodes: e.target.checked }))}
                className="mr-2"
              />
              <label htmlFor="includeTextNodes" className="text-sm text-gray-700">
                包含文本节点
              </label>
            </div>
            
            <div className="flex items-center space-x-2">
              <label htmlFor="arrayMode" className="text-sm text-gray-700">
                数组模式:
              </label>
              <select
                id="arrayMode"
                value={options.arrayMode}
                onChange={(e) => setOptions(prev => ({ ...prev, arrayMode: e.target.value as any }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="auto">自动</option>
                <option value="always">总是</option>
                <option value="never">从不</option>
              </select>
            </div>
            
            <div className="flex items-center space-x-2">
              <label htmlFor="indentSize" className="text-sm text-gray-700">
                缩进:
              </label>
              <select
                id="indentSize"
                value={options.indentSize}
                onChange={(e) => setOptions(prev => ({ ...prev, indentSize: parseInt(e.target.value) }))}
                className="px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value={2}>2空格</option>
                <option value={4}>4空格</option>
                <option value={8}>8空格</option>
              </select>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left: XML Input */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">XML 输入</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleSampleXml}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  示例
                </button>
                <button
                  onClick={handleClear}
                  className="px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                >
                  清空
                </button>
                <button
                  onClick={handleConvert}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
                >
                  转换
                </button>
              </div>
            </div>
            
            <textarea
              value={xmlInput}
              onChange={(e) => setXmlInput(e.target.value)}
              placeholder="在此输入XML数据..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
            />
            
            {error && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Right: JSON Output */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800">JSON 输出</h2>
              <div className="flex space-x-2">
                <button
                  onClick={handleCopyJson}
                  disabled={!jsonOutput}
                  className="px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  复制
                </button>
                <button
                  onClick={handleDownloadJson}
                  disabled={!jsonOutput}
                  className="px-3 py-2 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  下载
                </button>
              </div>
            </div>
            
            <textarea
              value={jsonOutput}
              readOnly
              placeholder="转换后的JSON将显示在这里..."
              className="w-full h-96 p-4 border border-gray-300 rounded-lg resize-none bg-gray-50 font-mono text-sm"
            />
          </div>
        </div>

        {/* Features */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">⚙️</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">灵活配置</h3>
            <p className="text-gray-600">多种转换选项，满足不同需求</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">🔍</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">智能解析</h3>
            <p className="text-gray-600">自动识别XML结构，生成标准JSON</p>
          </div>
          
          <div className="bg-white rounded-lg shadow-md p-6 text-center">
            <div className="text-3xl mb-3">📋</div>
            <h3 className="text-lg font-semibold text-gray-800 mb-2">便捷操作</h3>
            <p className="text-gray-600">支持复制、下载，提供示例数据</p>
          </div>
        </div>

        {/* Usage Tips */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-blue-800 mb-3">使用说明</h3>
          <ul className="text-blue-700 space-y-2 text-sm">
            <li>• 输入有效的XML数据，点击转换按钮</li>
            <li>• 可选择是否包含XML属性和文本节点</li>
            <li>• 支持自定义数组模式和缩进大小</li>
            <li>• 自动处理重复元素和嵌套结构</li>
            <li>• 生成的JSON会自动格式化，便于阅读</li>
            <li>• 可以复制JSON结果或下载为文件</li>
            <li>• 点击"示例"查看转换效果</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default XmlToJson;
